// Per-stage access model for the Bulk Editor (the numbered "Guided setup" flow
// in components/guided-setup.js). This is the SINGLE SOURCE OF TRUTH for who can
// open each of the nine stages — consumed by auth.js (runtime gate), the
// guided-setup card list, the Records "Bulk Editor" button, the roles guide, and
// the custom-role editor. Keys match the STAGES[].key in guided-setup.js exactly.
//
// Two role buckets per stage:
//   full   — whole-school, unscoped access to the stage.
//   scoped — access limited to the user's OWN students (their class / group /
//            year level / caseload), enforced by the editor filtering to
//            getVisibleStudents. A role in `scoped` (and not in `full`) sees the
//            stage but only their own kids.
//
// TOP_TEAM = the leadership tier that gets every stage, unscoped. `leadership`
// is the legacy alias for the owner/leadership role; `owner` is handled
// specially in auth.js (always full) so it isn't listed here.

export const BULK_TOP_TEAM = ['principal', 'ld_leader', 'executive', 'leadership'];

export const BULK_STAGES = [
  { key: 'students',           label: 'Student List (incl. photos)',  full: BULK_TOP_TEAM, scoped: [] },
  { key: 'photos',             label: 'Student photos',               full: BULK_TOP_TEAM, scoped: [] },
  { key: 'diagnoses',          label: 'Diagnosis, category & level',  full: BULK_TOP_TEAM, scoped: [] },
  { key: 'alliedHealth',       label: 'Allied health',                full: BULK_TOP_TEAM, scoped: ['grade_teacher'] },
  { key: 'timetable',          label: 'Timetables',                   full: BULK_TOP_TEAM, scoped: ['intervention', 'eso', 'eso_intervention'] },
  { key: 'interventions',      label: 'Intervention & support',      full: BULK_TOP_TEAM, scoped: ['intervention', 'eso', 'eso_intervention'] },
  // 'assessments' (score entry) stage removed — the Assessments page is the
  // single door. The stage below is the SCHEDULE (which columns a year level
  // has), not scores. Leadership-only and unscoped: it sets the shape of the
  // assessment page for the whole school, so it is not a per-class decision
  // and no scoped role gets it.
  { key: 'assessmentSchedule', label: 'Assessment schedule',     full: BULK_TOP_TEAM, scoped: [] },
  // Specialist teachers (PE, Music, Art, LOTE) teach the WHOLE school, so the
  // two specialist stages give the `specialist` role full, whole-school access —
  // a caseload scope would hide most of the classes they actually teach.
  { key: 'specialistSubjects', label: 'Specialist class subjects',    full: [...BULK_TOP_TEAM, 'specialist'], scoped: [] },
  { key: 'specialist',         label: 'Specialist class adjustments', full: [...BULK_TOP_TEAM, 'specialist'], scoped: [] },
  { key: 'attendance',         label: 'Attendance',                   full: BULK_TOP_TEAM, scoped: ['grade_teacher'] },
  // Signatures are sensitive (a staff member's actual signature) — restricted
  // to the LD leader + principal by default (owner always). Any other role,
  // incl. executive/leadership, needs it granted via a custom role.
  { key: 'signatures',         label: 'Staff signatures',             full: ['ld_leader', 'principal'], scoped: [] },
];

export function bulkStageDef(key) {
  return BULK_STAGES.find(s => s.key === key) || null;
}

// Pure role predicates (no current-user state) — auth.js wraps these with the
// signed-in role + owner/custom handling.
export function roleCanUseBulkStage(role, key) {
  const s = bulkStageDef(key);
  return !!s && (s.full.includes(role) || s.scoped.includes(role));
}

// True when the role only gets SCOPED (own-students) access to the stage.
export function roleBulkStageScoped(role, key) {
  const s = bulkStageDef(key);
  return !!s && s.scoped.includes(role) && !s.full.includes(role);
}

// Every role (matrix + legacy owner) that can use a stage — for the roles guide.
export function rolesForBulkStage(key) {
  const s = bulkStageDef(key);
  if (!s) return [];
  return ['owner', ...s.full.filter(r => r !== 'leadership'), ...s.scoped];
}
